//
// singleCaseStore
//
// Stores more information concerning a single case
//
// refreshSingleCase cases=[
// {
//   "csid": "BBmLkou3xjaY6AgBVmDU",
//   "category": "police",
//   "fake": 1,
//   "ctime": "2020-07-08 17:40:53.0",
//   "ltime": "2020-07-08 17:40:57.0",
//   "emergency": null,
//   "lat": null,
//   "lng": null,
// },
//
import PropTypes from 'prop-types';
import {
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useMemo,
} from 'react';

import {
  compressCasePicture,
  config,
  partnerLogoUrl,
  participantNamesAndColor,
  displayJson,
  removeNullObjectProperties,
  objectsIdentical,
  isoDatetimeUtc,
  useIntervalAtMost,
  logError,
} from '../../utils';
import useAuth from '../auth/useAuth';
import useCalls from '../calls/useCalls';
import useCaseRefreshOnPush from '../push/useCaseRefreshOnPush';
import usePush from '../push/usePush';
import useTownAssets from '../townAssets/useTownAssets';
import useTownConfig from '../townConfig/useTownConfig';

import SingleCaseContext from './SingleCaseContext';
import singleCaseContextDefaultValue from './singleCaseContextDefaultValue';

const refreshPeriodS = 4;
const caseInfoRefreshDebounceMs = 800;

const initialState = {
  ready: false,
  piid: undefined,
  logoId: undefined,
  prtnrWideLogoId: undefined,
  csid: undefined,
  thisCase: {},
  initPending: false,
  gatheringsList: undefined,
  selectedGthrId: undefined,
  selectedPublic: false,
  commandPending: false,
  refreshPending: false,
  loadError: false,
};

const stateReducer = (log) => (state, action) => {
  // console.log(`singleCaseStore ${log} stateReducer ${displayJson(action)}`);
  switch (action.type) {
    case 'initFetch':
      if (state.initPending && (action.csid === state.csid)) return state;

      if (log) {
        console.log(`singleCaseStore ${log} initFetch ${displayJson(action)
        }, ready=${state.ready}, initPending=${state.initPending}, csid=${
          state.csid}, piid=${state.piid}`);
      }

      return {
        ...initialState,
        initPending: true,
        refreshPending: true,
        csid: action.csid,
        piid: action.piid,
        logoId: action.logoId,
        prtnrWideLogoId: action.prtnrWideLogoId,
      };

    case 'setCase':
      if (!action.case.csid) {
        logError(
          `singleCaseStore ${log} stateReducer setCase action=${displayJson(action)}`,
          'SingleCaseProvider.reducer.setCase.missingCsid',
          {
            log,
            stateCsid: state?.csid,
            actionKeys: action?.case && Object.keys(action.case),
          },
        );
        throw new Error('SingleCaseProvider setCase missing action.case.csid');
      }

      if (state.csid
        && action.case.csid
        && (state.csid !== action.case.csid)) {
        console.log(`Dropping setCase on wrong case, current state.csid=${
          state.csid}, update ${action.case.csid}`);
        return state;
      }

      if (log && (!state.ready)) {
        // console.log(`Current state.csid=${
        //   state.csid}, setCase ${action.case.csid}`);
        console.log(`singleCaseStore ${log} loading case ${displayJson(action.case)}`);
      }

      // console.log(`singleCaseStore ${log} thisCase=${displayJson(action.case)}`);
      return {
        ...state,
        thisCase: action.case,
        ready: true,
        refreshPending: false,
        initPending: false,
        loadError: false,
      };

    case 'caseUnavailable':
      // The server answers a getInfo on a case we have no standing on with a
      // status row instead of a case, typically a stale or shared link
      return {
        ...state,
        thisCase: {},
        ready: false,
        initPending: false,
        refreshPending: false,
        loadError: true,
      };

    case 'adjustCase':
      //
      // Commands modifying the case send back accepted modifications,
      // integrate them in the case
      //
      if (!action.case.csid) {
        console.log(`singleCaseStore ${log} stateReducer adjustCase action=${displayJson(action)}`);
        throw new Error('SingleCaseProvider adjustCase missing action.case.csid');
      }

      if (state.ready && (state.csid !== action.case.csid)) {
        console.log(`Drop update on wrong case, current csid=${
          state.csid}, update ${action.csid}`);
        return state;
      }

      // console.log(`singleCaseStore ${log} thisCase=${displayJson(action.case)}`);
      return {
        ...state,
        thisCase: {
          ...state.thisCase,
          ...action.case,
        },
      };

    case 'commandStart': {
      // console.log(`singleCaseStore ${log} stateReducer ${displayJson(action)}`);
      if (state.commandPending) return state;
      return {
        ...state,
        commandPending: true,
      };
    }

    case 'commandEnd': {
      // console.log(`singleCaseStore ${log} stateReducer ${displayJson(action)}`);
      if (!state.commandPending) return state;
      return {
        ...state,
        commandPending: false,
      };
    }

    case 'refreshStart': {
      if (state.refreshPending) return state;
      return {
        ...state,
        refreshPending: true,
        loadError: false,
      };
    }

    case 'refreshError': {
      if (!state.refreshPending) return state;
      return {
        ...state,
        // ready: false,
        refreshPending: false,
        loadError: true,
      };
    }

    case 'fetchGatheringsList': {
      if (state.fetchGatheringsListPending) return state;
      return {
        ...state,
        fetchGatheringsListPending: true,
      };
    }

    case 'endFetchGatheringsList': {
      if (!action.csid) {
        throw new Error(
          'SingleCaseProvider endFetchGatheringsList missing action.csid',
        );
      }

      if (!state.ready) return state;

      if (state.csid !== action.csid) {
        console.log(`endFetchGatheringsList on wrong case, current csid=${
          state.csid}, update ${action.csid}`);
        return state;
      }

      // console.log(`singleCaseStore ${log} gatheringsList=${displayJson(action.list)}`);
      if (!state.fetchGatheringsListPending) return state;
      if (objectsIdentical(state.gatheringsList, action.list, 1)) return state;

      // console.log(`singleCaseStore gatheringsList=${displayJson(action.list)}`);

      return {
        ...state,
        fetchGatheringsListPending: false,
        gatheringsList: action.list,
      };
    }

    case 'createGatheringStart': {
      if (state.commandPending) return state;
      return {
        ...state,
        commandPending: true,
      };
    }

    case 'createGatheringEnd': {
      if (!state.commandPending) return state;
      return {
        ...state,
        commandPending: false,
      };
    }

    case 'selectGathering': {
      const { gthrId } = action;
      if (state.selectedGthrId === gthrId) return state;

      // Guard against a stale id (e.g. a click on a gathering dropped by the
      // last list refresh): keep the current selection instead of blanking it.
      if (!state.gatheringsList?.some((thisGathering) => (thisGathering.gthrId === gthrId))) {
        logError(
          `gthrId=${gthrId} not in gatheringsList`,
          'SingleCaseProvider.reducer.selectGathering.unknownGthrId',
          {
            gthrId,
            csid: state.csid,
            knownGthrIds: state.gatheringsList?.map?.((thisGathering) => (thisGathering.gthrId)),
          },
        );
        return state;
      }

      return {
        ...state,
        selectedGthrId: gthrId,
      };
    }

    case 'setSelectedPublic': {
      if (state.selectedPublic === action.public) return state;
      return {
        ...state,
        selectedPublic: action.public,
      };
    }

    default:
      throw new Error(`singleCaseStore stateReducer action=${displayJson(action)}`);
  }
};

function SingleCaseProvider(props) {
  const {
    children,
    caseSelected,
    log,
    cstmrType,
    onCaseUnavailable,
  } = props;
  const { calls } = useCalls();
  const { auth } = useAuth();
  const { townConfig } = useTownConfig();
  const caseAutoJoin = townConfig.settings?.caseAutoJoin === true;
  const {
    cstmrPost,
    cstmrPostBlob,
    info,
    partnersMap,
  } = auth;
  const {
    rlid,
  } = info;
  const { assets } = useTownAssets();
  const [state, dispatch] = useReducer(stateReducer(log), initialState);
  // Maintain a simplified list of participants for chat purposes, update
  // it only when needed
  const chatParticipantsRef = useRef([]);
  // Held in a ref so a caller passing an inline arrow does not rebuild
  // refreshSingleCase, and fired once per case as the poll keeps running
  const onCaseUnavailableRef = useRef(onCaseUnavailable);
  onCaseUnavailableRef.current = onCaseUnavailable;
  const caseUnavailableCsidRef = useRef(undefined);
  const {
    translate,
  } = assets;
  const {
    // cuid,
    csid,
    piid,
    ready,
  } = state;
  const { push } = usePush();
  const { wsConnected } = push;

  useEffect(
    () => { console.log(`SingleCaseProvider caseSelected=${displayJson(caseSelected)}`); },
    [caseSelected],
  );

  const participateCase = useCallback(
    async () => {
      let error;
      let res;
      try {
        // console.log(`Particpate in csid=${csid} at ${url}`);
        res = await cstmrPost({
          piid,
          rlid,
          urlPath: config.town.back.cases.participate,
          payload: {
            csid,
            handler: true,
            ctime: isoDatetimeUtc(0, 1),
            eventCapturedAt: performance.now(),
          },
        });
        if (res.status !== 200) {
          error = new Error(`Response status=${res.status}`);
        }
      } catch (err) {
        error = err;
      }

      if (!error) {
        // console.log(`participateCase res.body.rslt=${
        //   displayJson(res.body.rslt, 4)
        // }`);
        // console.log(`new crUid=${res.body.rslt.data[0].crUid}`);
        return (res.body.rslt.data[0]);
      }
      logError(
        `participateCase Error:${error?.message ?? error}`, // NOSONAR error stringified for logging
        'SingleCaseProvider.participateCase.error',
        {
          csid,
          err: error?.message ?? String(error), // NOSONAR error stringified for logging
          ...(error?.status && { status: error.status }),
        },
      );
      return (false);
    },
    [csid, piid, rlid, cstmrPost],
  );

  // On a caseAutoJoin customer joining is not a step of its own: the first
  // action a responder takes on a case puts them on it, so every command runs
  // through here first. Answers this responder's caseRole id on the case, or
  // null when they are not on it. Deliberately outside the commandPending
  // bracket, which the calling command owns.
  // Commands fired before the join lands share the one request: crUid only
  // reaches state a render later, so on its own each command would post its
  // own /participate and the case history would show several arrivals.
  const joinRequestRef = useRef({});

  const ensureJoined = useCallback(
    async () => {
      // A staff role already on the case without the handler flag (a task
      // party) still has to join: /cases/handling refuses non-handlers
      if (state.thisCase.crUid && state.thisCase.crHandler) return state.thisCase.crUid;
      if ((!caseAutoJoin) || (!state.ready)) return (state.thisCase.crUid ?? null);

      const join = (joinRequestRef.current.csid === csid) ? joinRequestRef.current : { csid };
      joinRequestRef.current = join;

      join.sent ??= participateCase().then((rslt) => {
        // A failed join must not stick: the next command tries again.
        if (!rslt) join.sent = null;
        else {
          dispatch({
            type: 'adjustCase',
            case: {
              piid,
              csid,
              ...rslt,
              ...(rslt.crUid && { crHandler: 1 }),
            },
          });
        }
        return rslt;
      });

      const rslt = await join.sent;
      return (rslt) ? (rslt.crUid ?? state.thisCase.crUid ?? null) : (state.thisCase.crUid ?? null);
    },
    [caseAutoJoin, state.ready, state.thisCase.crUid, state.thisCase.crHandler, participateCase, piid, csid],
  );

  const holdCase = useCallback(
    async () => {
      let error;
      let res;
      try {
        // console.log(`Hold csid=${csid} at ${url}`);
        res = await cstmrPost({
          piid,
          rlid,
          urlPath: config.town.back.cases.handling,
          payload: {
            csid,
            ctime: isoDatetimeUtc(0, 1),
            eventCapturedAt: performance.now(),
            changes: {
              hold: true,
            },
          },
        });
        if (res.status !== 200) {
          error = new Error(`Response status=${res.status}`);
        }
      } catch (err) {
        error = err;
      }

      if (!error) {
        // console.log(`holdCase res.body.rslt=${
        //   displayJson(res.body.rslt, 4)
        // }`);
        return (res.body.rslt.data[0]);
      }
      logError(
        `holdCase Error:${error?.message ?? error}`, // NOSONAR error stringified for logging
        'SingleCaseProvider.holdCase.error',
        {
          csid,
          err: error?.message ?? String(error), // NOSONAR error stringified for logging
          ...(error?.status && { status: error.status }),
        },
      );
      return false;
    },
    [csid, piid, rlid, cstmrPost],
  );

  const changeCase = useCallback(
    async (chng) => {
      let error;
      let res;
      try {
        res = await cstmrPost({
          piid,
          rlid,
          urlPath: config.town.back.cases.change,
          payload: {
            csid,
            ctime: isoDatetimeUtc(0, 1),
            eventCapturedAt: performance.now(),
            chng,
          },
        });
        if (res.status !== 200) {
          error = new Error(`Response status=${res.status}`);
        }
      } catch (err) {
        error = err;
      }

      if (!error) {
        return (res.body.rslt.data[0]);
      }
      logError(
        `changeCase Error:${error?.message ?? error}`, // NOSONAR error stringified for logging
        'SingleCaseProvider.changeCase.error',
        {
          csid,
          err: error?.message ?? String(error), // NOSONAR error stringified for logging
          ...(error?.status && { status: error.status }),
        },
      );
      return false;
    },
    [csid, piid, rlid, cstmrPost],
  );

  // A picture on a case: compressed here so an oversized upload never reaches
  // the API, and carrying the task step when the picture is a proof — one
  // message records both, so an upload that lands cannot leave its step unsaid.
  const sendCasePicture = useCallback(
    async (
      file,
      {
        gthrId,
        note,
        task,
        rotation,
      } = {},
    ) => {
      const picture = await compressCasePicture(file, { rotation });
      if (!picture) return false;

      await ensureJoined();

      try {
        const info = {
          csid,
          msgCdatim1: isoDatetimeUtc(0, 1),
          eventCapturedAt: performance.now(),
          msgType: 'photo',
          fileInfo: {},
          gthrId,
        };
        if (note) info.note = note;
        if (task) info.task = task;

        const res = await cstmrPostBlob({
          piid,
          rlid,
          urlPath: config.town.back.discuss.send.media,
          headers: ['Accept', 'application/json'],
          info,
          blob: picture,
        });

        if (res.status !== 200) {
          console.log(`sendCasePicture status=${res.status}`); // NOSONAR status logged
          return false;
        }
        return (res.body?.rslt ?? true);
      } catch (err) {
        logError(
          `sendCasePicture Error:${err?.message ?? err}`, // NOSONAR error stringified for logging
          'SingleCaseProvider.sendCasePicture.error',
          {
            csid,
            err: err?.message ?? String(err), // NOSONAR error stringified for logging
            ...(err?.status && { status: err.status }),
          },
        );
        return false;
      }
    },
    [csid, piid, rlid, cstmrPostBlob, ensureJoined],
  );

  const changeTaskRequest = useCallback(
    async (task, chngLvl) => {
      let error;
      let res;
      try {
        res = await cstmrPost({
          piid,
          rlid,
          urlPath: config.town.back.tasks.change,
          payload: {
            csid,
            ctime: isoDatetimeUtc(0, 1),
            eventCapturedAt: performance.now(),
            chng: { task },
            chngLvl,
          },
        });
        if (res.status !== 200) {
          error = new Error(`Response status=${res.status}`);
        }
      } catch (err) {
        error = err;
      }

      if (!error) {
        return (res.body.rslt.data[0]);
      }
      logError(
        `changeTask Error:${error?.message ?? error}`, // NOSONAR error stringified for logging
        'SingleCaseProvider.changeTask.error',
        {
          csid,
          err: error?.message ?? String(error), // NOSONAR error stringified for logging
          ...(error?.status && { status: error.status }),
        },
      );
      return false;
    },
    [csid, piid, rlid, cstmrPost],
  );

  const rejectCase = useCallback(
    async () => {
      let error;
      let res;
      try {
        // console.log(`Reject csid=${csid} at ${url}`);
        res = await cstmrPost({
          piid,
          rlid,
          urlPath: config.town.back.cases.handling,
          payload: {
            csid,
            ctime: isoDatetimeUtc(0, 1),
            eventCapturedAt: performance.now(),
            changes: {
              reject: true,
            },
          },
        });
        if (res.status !== 200) {
          error = new Error(`Response status=${res.status}`);
        }
      } catch (err) {
        error = err;
      }

      if (!error) {
        // console.log(`rejectCase res.body.rslt=${
        //   displayJson(res.body.rslt, 4)
        // }`);
        return (res.body.rslt.data[0]);
      }
      logError(
        `rejectCase Error:${error?.message ?? error}`, // NOSONAR error stringified for logging
        'SingleCaseProvider.rejectCase.error',
        {
          csid,
          err: error?.message ?? String(error), // NOSONAR error stringified for logging
          ...(error?.status && { status: error.status }),
        },
      );
      return false;
    },
    [csid, piid, rlid, cstmrPost],
  );

  // Block / unblock a case participant. answrToMsgId is set only when the
  // block is raised from a specific chat message.
  const blockUser = useCallback(
    async ({
      tgtCrUid, block, reason, answrToMsgId,
    }) => {
      let error;
      let res;

      await ensureJoined();

      try {
        res = await cstmrPost({
          piid,
          rlid,
          urlPath: config.town.back.users.block,
          payload: {
            csid,
            block,
            tgtCrUid,
            ...(block ? { reason } : {}),
            ...((block && answrToMsgId) ? { answrToMsgId } : {}),
          },
        });
        if (res.status !== 200) {
          error = new Error(`Response status=${res.status}`);
        }
      } catch (err) {
        error = err;
      }

      if (!error) {
        // /users/block answers with middlewAnswerOk (no rslt.data payload).
        return (res.body?.rslt?.data?.[0] ?? true);
      }
      logError(
        `blockUser Error:${error?.message ?? error}`, // NOSONAR error stringified for logging
        'SingleCaseProvider.blockUser.error',
        {
          csid,
          err: error?.message ?? String(error), // NOSONAR error stringified for logging
          ...(error?.status && { status: error.status }),
        },
      );
      return false;
    },
    [csid, piid, rlid, cstmrPost, ensureJoined],
  );

  const leaveCase = useCallback(
    async () => {
      let error;
      let res;
      try {
        // console.log(`Reject csid=${csid} at ${url}`);
        res = await cstmrPost({
          piid,
          rlid,
          urlPath: config.town.back.cases.leave,
          payload: {
            csid,
            ctime: isoDatetimeUtc(0, 1),
            eventCapturedAt: performance.now(),
          },
        });
        if (res.status !== 200) {
          error = new Error(`Response status=${res.status}`);
        }
      } catch (err) {
        error = err;
      }

      if (!error) {
        // console.log(`leaveCase res.body.rslt=${
        //   displayJson(res.body.rslt, 4)
        // }`);
        return (true);
      }
      logError(
        `leaveCase Error:${error?.message ?? error}`, // NOSONAR error stringified for logging
        'SingleCaseProvider.leaveCase.error',
        {
          csid,
          err: error?.message ?? String(error), // NOSONAR error stringified for logging
          ...(error?.status && { status: error.status }),
        },
      );
      return false;
    },
    [csid, piid, rlid, cstmrPost],
  );

  const rejoinCase = useCallback(
    async () => {
      let error;
      let res;
      try {
        // console.log(`Resume csid=${csid} at ${url}`);
        res = await cstmrPost({
          piid,
          rlid,
          urlPath: config.town.back.cases.handling,
          payload: {
            csid,
            ctime: isoDatetimeUtc(0, 1),
            eventCapturedAt: performance.now(),
            changes: {
              leave: false,
            },
          },
        });
        if (res.status !== 200) {
          error = new Error(`Response status=${res.status}`);
        }
      } catch (err) {
        error = err;
      }

      if (!error) {
        // console.log(`rejoinCase res.body.rslt=${
        //   displayJson(res.body.rslt, 4)
        // }`);
        return (res.body.rslt.data[0]);
      }
      logError(
        `rejoinCase Error:${error?.message ?? error}`, // NOSONAR error stringified for logging
        'SingleCaseProvider.rejoinCase.error',
        {
          csid,
          err: error?.message ?? String(error), // NOSONAR error stringified for logging
          ...(error?.status && { status: error.status }),
        },
      );
      return false;
    },
    [csid, piid, rlid, cstmrPost],
  );

  const closeCase = useCallback(
    async () => {
      let error;
      let res;
      try {
        // console.log(`Close csid=${csid} at ${url}`);
        res = await cstmrPost({
          piid,
          rlid,
          urlPath: config.town.back.cases.handling,
          payload: {
            csid,
            ctime: isoDatetimeUtc(0, 1),
            eventCapturedAt: performance.now(),
            changes: {
              close: true,
            },
          },
        });
        if (res.status !== 200) {
          error = new Error(`Response status=${res.status}`);
        }
      } catch (err) {
        error = err;
      }

      if (!error) {
        // console.log(`closeCase res.body.rslt=${
        //   displayJson(res.body.rslt, 4)
        // }`);
        return (res.body.rslt.data[0]);
      }
      logError(
        `closeCase Error:${error?.message ?? error}`, // NOSONAR error stringified for logging
        'SingleCaseProvider.closeCase.error',
        {
          csid,
          err: error?.message ?? String(error), // NOSONAR error stringified for logging
          ...(error?.status && { status: error.status }),
        },
      );
      return false;
    },
    [csid, piid, rlid, cstmrPost],
  );

  const cancelCase = useCallback(
    async () => {
      let error;
      let res;
      try {
        res = await cstmrPost({
          piid,
          rlid,
          urlPath: config.town.back.cases.handling,
          payload: {
            csid,
            ctime: isoDatetimeUtc(0, 1),
            eventCapturedAt: performance.now(),
            changes: {
              crCancel: 1,
            },
          },
        });
        if (res.status !== 200) {
          error = new Error(`Response status=${res.status}`);
        }
      } catch (err) {
        error = err;
      }

      if (!error) {
        return (res.body.rslt.data[0]);
      }
      logError(
        `cancelCase Error:${error?.message ?? error}`, // NOSONAR error stringified for logging
        'SingleCaseProvider.cancelCase.error',
        {
          csid,
          err: error?.message ?? String(error), // NOSONAR error stringified for logging
          ...(error?.status && { status: error.status }),
        },
      );
      return false;
    },
    [csid, piid, rlid, cstmrPost],
  );

  const resumeCase = useCallback(
    async () => {
      let error;
      let res;
      try {
        // console.log(`Resume csid=${csid} at ${url}`);
        res = await cstmrPost({
          piid,
          rlid,
          urlPath: config.town.back.cases.handling,
          payload: {
            csid,
            ctime: isoDatetimeUtc(0, 1),
            eventCapturedAt: performance.now(),
            changes: {
              close: false,
              hold: false,
              crCancel: 0,
            },
          },
        });
        if (res.status !== 200) {
          error = new Error(`Response status=${res.status}`);
        }
      } catch (err) {
        error = err;
      }

      if (!error) {
        // console.log(`resumeCase res.body.rslt=${
        //   displayJson(res.body.rslt, 4)
        // }`);
        return (res.body.rslt.data[0]);
      }
      logError(
        `resumeCase Error:${error?.message ?? error}`, // NOSONAR error stringified for logging
        'SingleCaseProvider.resumeCase.error',
        {
          csid,
          err: error?.message ?? String(error), // NOSONAR error stringified for logging
          ...(error?.status && { status: error.status }),
        },
      );
      return false;
    },
    [csid, piid, rlid, cstmrPost],
  );

  const clearCaseEmergency = useCallback(
    async (ackCrUid) => {
      let error;
      let res;
      try {
        // console.log(`Resume csid=${csid} at ${url}`);
        res = await cstmrPost({
          piid,
          rlid,
          urlPath: config.town.back.cases.clearEmergency,
          payload: {
            csid,
            ctime: isoDatetimeUtc(0, 1),
            eventCapturedAt: performance.now(),
            ackCrUid,
          },
        });
        if (res.status !== 200) {
          error = new Error(`Response status=${res.status}`);
        }
      } catch (err) {
        error = err;
      }

      if (error) {
        logError(
          `clearCaseEmergency Error:${error?.message ?? error}`, // NOSONAR error stringified for logging
          'SingleCaseProvider.clearCaseEmergency.error',
          {
            csid,
            err: error?.message ?? String(error), // NOSONAR error stringified for logging
            ...(error?.status && { status: error.status }),
          },
        );
        return false;
      }

      return true;
    },
    [csid, piid, rlid, cstmrPost],
  );

  const getGatheringsList = useCallback(
    async () => {
      if (!csid) {
        console.log(`SingleCaseProvider - get discussions list for csid=${csid} - ignore`);
        return null;
      }

      // console.log(`Get discussions list for csid=${csid}`);
      dispatch({ type: 'fetchGatheringsList' });
      let error;
      let res;
      try {
        // console.log(`SingleCaseProvider Get discussions list for csid=${csid} at ${url}`);
        res = await cstmrPost({
          piid,
          rlid,
          urlPath: config.town.back.gatherings.listAll,
          payload: {
            csid,
          },
        });
        if (res.status !== 200) {
          error = new Error(`Response status=${res.status}`);
        }
      } catch (err) {
        error = err;
      }

      if (!error) {
        // console.log(`SingleCaseProvider getGatheringsList ${
        //   res.body.rslt.data.length} discussions`);
        // console.log(`SingleCaseProvider getGatheringsList discussions=${
        //   displayJson(res.body.rslt.data, 4)
        // }`);
        // console.log(`refreshUserMainMenu code=${res.body.rslt.data[0].police.code
        // } categories=${displayJson(res.body.rslt.data[0].police.sub, 2)}`);

        return dispatch({
          type: 'endFetchGatheringsList',
          csid,
          list: res.body.rslt.data,
        });
      }
      logError(
        `getGatheringsList Error:${error?.message ?? error}`, // NOSONAR error stringified for logging
        'SingleCaseProvider.getGatheringsList.error',
        {
          csid,
          err: error?.message ?? String(error), // NOSONAR error stringified for logging
          ...(error?.status && { status: error.status }),
        },
      );
      return null;
    },
    [csid, piid, rlid, cstmrPost],
  );

  const setCaseVideoEn = useCallback(
    async (videoEn) => {
      let error;
      let res;
      try {
        // console.log(`Close csid=${csid} at ${url}`);
        res = await cstmrPost({
          piid,
          rlid,
          urlPath: config.town.back.cases.enableVideo,
          payload: {
            csid,
            ctime: isoDatetimeUtc(0, 1),
            eventCapturedAt: performance.now(),
            videoEn,
          },
        });
        if (res.status !== 200) {
          error = new Error(`Response status=${res.status}`);
        }
      } catch (err) {
        error = err;
      }

      if (!error) {
        // console.log(`setCaseVideoEn res.body.rslt=${
        //   displayJson(res.body.rslt, 4)
        // }`);
        return true;
      }
      logError(
        `setCaseVideoEn Error:${error?.message ?? error}`, // NOSONAR error stringified for logging
        'SingleCaseProvider.setCaseVideoEn.error',
        {
          csid,
          err: error?.message ?? String(error), // NOSONAR error stringified for logging
          ...(error?.status && { status: error.status }),
        },
      );
      return false;
    },
    [csid, piid, rlid, cstmrPost],
  );

  const createAlert = useCallback(
    async (params) => {
      const {
        alrtEDatim,
        alrtAnon,
        alrtPolyLngLat,
        alrtLat,
        alrtLng,
        alrtRadiusM,
        alrtTxt,
        alrtForm,
        mediaMsgId,
      } = params;
      // console.log(`Get discussions list for csid=${csid}`);
      let error;
      let res;
      const body = {
        csid,
        alrtCDatim: isoDatetimeUtc(),
        alrtEDatim,
        alrtAnon,
        alrtPolyLngLat,
        alrtLat,
        alrtLng,
        alrtRadiusM,
        alrtTxt,
        alrtForm,
        mediaMsgId,
      };
      removeNullObjectProperties(body, true);

      await ensureJoined();

      try {
        const urlPath = config.town.back.alerts.create;

        // console.log(`SingleCaseProvider Get discussions list for csid=${csid} at ${url}`);
        res = await cstmrPost({
          piid,
          rlid,
          urlPath,
          payload: body,
        });
        if (res.status !== 200) {
          error = new Error(`Response status=${res.status}`);
        }
      } catch (err) {
        error = err;
      }

      if (error) {
        logError(
          `createAlert Error:${error?.message ?? error}`, // NOSONAR error stringified for logging
          'SingleCaseProvider.createAlert.error',
          {
            csid,
            err: error?.message ?? String(error), // NOSONAR error stringified for logging
            ...(error?.status && { status: error.status }),
          },
        );
      }
    },
    [csid, cstmrPost, piid, rlid, ensureJoined],
  );

  const refreshSingleCase = useCallback(
    async (
      refreshPiid,
      refreshCsid,
    ) => {
      let error;
      let res;

      if (!refreshCsid) return;

      // console.log('refreshSingleCase start');
      dispatch({ type: 'refreshStart' });

      try {
        const urlPath = config.town.back.cases.getInfo;

        // console.log(`singleCaseStore ${log}, get info at ${url} for csid=${csid}`);
        res = await cstmrPost({
          piid: refreshPiid,
          rlid,
          urlPath,
          payload: {
            csid: refreshCsid,
          },
        });
        if (res.status !== 200) {
          error = new Error(`Response status=${res.status}`);
        }
      } catch (err) {
        error = err;
      }

      if (!error) {
        if ((!res.body.rslt.data[0]) || res.body.rslt.data[0].unknownCase) {
          dispatch({ type: 'caseUnavailable' });
          if (caseUnavailableCsidRef.current !== refreshCsid) {
            caseUnavailableCsidRef.current = refreshCsid;
            onCaseUnavailableRef.current?.(refreshCsid);
          }
          return;
        }

        // console.log(`refreshSingleCase got ${res.body.rslt.data.length} cases`);
        // console.log(`refreshSingleCase case=${
        //   displayJson(res.body.rslt.data[0])
        // }`);
        // console.log(`refreshSingleCase csid=${
        //   res.body.rslt.data[0].csid} ccatCode=${res.body.rslt.data[0].ccatCode}`);

        let newParticipants;
        if (res.body.rslt.data[0].participants) {
          newParticipants = res.body.rslt.data[0].participants
            .map((participant) => {
              const {
                name,
                initials,
                shortName,
                color,
                // txtColor,
              } = participantNamesAndColor(translate, participant, cstmrType);

              const {
                crBlocked,
                isExternal,
                logoId,
                crFname,
                crLname,
                crDesc,
                crLDatim,
              } = participant;

              const ret = {
                name,
                initials,
                shortName,
                color,
                grpGpid: participant.grpGpid,
                grpLbl: participant.grpLbl,
                gcatCode: participant.gcatCode,
                crUid: participant.crUid,
                crRlid: participant.crRlid,
                iAm: participant.iAm || [],
                object: (participant.iAm)
                  ? participant.iAm.filter((thisProp) => (thisProp.startsWith('q_')))
                  : [],
                supports: (participant.iAm)
                  ? participant.iAm.filter((thisProp) => (thisProp.startsWith('s_')))
                      .map((thisCrUid) => (Number.parseInt(thisCrUid.substring(2), 10)))
                  : [],
              };

              if (logoId) ret.logoId = participant.logoId;
              if (crFname) ret.crFname = participant.crFname;
              if (crLname) ret.crLname = participant.crLname;
              if (crDesc) ret.crDesc = participant.crDesc;
              if (crLDatim) ret.crLDatim = participant.crLDatim;
              if (crBlocked) ret.crBlocked = crBlocked;
              if (isExternal) {
                ret.isExternal = isExternal;

                const {
                  cuid, cstmrLbl, logoId, wideLogoId, backToCstmr,
                } = isExternal;

                let prtnrLogoUrl;
                let prtnrLbl = cstmrLbl;

                if (cstmrLbl) {
                  // We have all info concerning this partner, including its logo(s)
                  prtnrLogoUrl = partnerLogoUrl(backToCstmr, cuid, logoId, wideLogoId);
                } else {
                  const partnership = partnersMap?.[refreshPiid];

                  prtnrLogoUrl = partnerLogoUrl(
                    partnership?.backUrl,
                    cuid,
                    partnership?.logoId,
                    partnership?.wideLogoId,
                  );
                  prtnrLbl = partnership?.lbl;
                }

                if (prtnrLogoUrl) ret.prtnrLogoUrl = prtnrLogoUrl;
                if (prtnrLbl) ret.prtnrLbl = prtnrLbl;
              }

              return ret;
            });

          // Provide raw list as a ref, as to not trigger unnecessary rendering
          chatParticipantsRef.current = newParticipants;
        } else newParticipants = [];

        const newGrp = res.body.rslt.data[0].grp
          ?.map((thisGrp) => {
            const { isExternal } = thisGrp;
            if (!isExternal) return thisGrp;

            const {
              cuid, cstmrLbl, logoId, wideLogoId, backToCstmr,
            } = isExternal;

            let prtnrLogoUrl;
            let prtnrLbl = cstmrLbl;

            if (cstmrLbl) {
              // We have all info concerning this partner, including its logo(s)
              prtnrLogoUrl = partnerLogoUrl(backToCstmr, cuid, logoId, wideLogoId);
            } else {
              const partnership = partnersMap?.[refreshPiid];

              prtnrLogoUrl = partnerLogoUrl(
                partnership?.backUrl,
                cuid,
                partnership?.logoId,
                partnership?.wideLogoId,
              );
              prtnrLbl = partnership?.lbl;
            }

            const ret = { ...thisGrp };
            if (prtnrLogoUrl) ret.prtnrLogoUrl = prtnrLogoUrl;
            if (prtnrLbl) ret.prtnrLbl = prtnrLbl;

            return ret;
          });

        const {
          csClose, csHold, csUsersCnt, csUsersDone, csHandlers, crLDatim,
          csCancel, csReject,
        } = res.body.rslt.data[0];

        let status;
        if (csReject) {
          status = 'rejected';
        } else if (csClose) {
          status = 'closed';
        } else if (csCancel) {
          status = 'canceled';
        } else if (csHold) {
          status = 'paused';
        } else if (crLDatim) {
          status = 'left';
        } else if (csUsersCnt && (csUsersDone === csUsersCnt)) {
          status = 'closedByUsers';
        } else if (!csHandlers) {
          status = 'notHandled';
        } else {
          status = 'active';
        }

        dispatch({
          type: 'setCase',
          case: {
            ...res.body.rslt.data[0],
            participants: newParticipants,
            grp: newGrp,
            status,
            piid: refreshPiid,
          },
        });

        return;
      }
      logError(
        `refreshSingleCase Error:${error?.message ?? error}`, // NOSONAR error stringified for logging
        'SingleCaseProvider.refreshSingleCase.error',
        {
          csid: refreshCsid,
          err: error?.message ?? String(error), // NOSONAR error stringified for logging
          ...(error?.status && { status: error.status }),
        },
      );
      dispatch({ type: 'refreshError' });
    },
    [cstmrPost, cstmrType, translate, rlid, partnersMap],
  );

  const updateEvntIdFetched = useCallback(
    async (evntIdFetched) => {
      let error;
      let res;

      if (!csid) return;
      // console.log('updateEvntIdFetched start');
      try {
        const urlPath = config.town.back.discuss.fetch.flag;

        // console.log(`singleCaseStore ${log}, get info at ${url} for csid=${csid}`);
        res = await cstmrPost({
          piid,
          rlid,
          urlPath,
          payload: {
            csid, evntIdFetched,
          },
        });
        if (res.status !== 200) {
          error = new Error(`Response status=${res.status}`);
        }
      } catch (err) {
        error = err;
      }

      if (error) {
        logError(
          `updateEvntIdFetched Error:${error?.message ?? error}`, // NOSONAR error stringified for logging
          'SingleCaseProvider.updateEvntIdFetched.error',
          {
            csid,
            err: error?.message ?? String(error), // NOSONAR error stringified for logging
            ...(error?.status && { status: error.status }),
          },
        );
      }
    },
    [csid, cstmrPost, piid, rlid],
  );

  const createGathering = useCallback(
    async (
      gthrTypes,
      gthrInterUser = false,
      gthrLbl = undefined,
    ) => {
      // console.log(`SingleCaseProvider createGathering gthrTypes=${JSON.stringify(gthrTypes)}`);
      if ((!state.ready)
        || (!state.gatheringsList)
      ) {
        console.log(`Cannot create gathering gthrTypes=${JSON.stringify(gthrTypes)}`);
        console.log(`  state.ready=${JSON.stringify(state.ready)}`);
        // console.log(`  gatheringsList=${JSON.stringify(state.gatheringsList)}`);
        return null;
      }

      let error;
      let res;
      try {
        const urlPath = config.town.back.gatherings.create;
        // console.log(`SingleCaseProvider createGathering at "${url}"`);
        const payload = {
          gthrLbl,
          csid,
          gthrTypes,
          gthrInterUser: (gthrInterUser) ? 1 : 0,
        };
        removeNullObjectProperties(payload);
        res = await cstmrPost({
          piid,
          rlid,
          urlPath,
          payload,
        });
        if (res.status !== 200) {
          error = new Error(`createGathering response status=${res.status}`);
        }
      } catch (err) {
        console.log(`createGathering err=${err}`);
        error = err;
      }

      if (!error) {
        console.log(`SingleCaseProvider createGathering ok ${
          displayJson({
            gthrTypes,
            gthrInterUser,
            gthrLbl,
          })}`);
        // console.log(`SingleCaseProvider createGathering res.body.rslt=${
        //   JSON.stringify(res.body.rslt)}`);

        await getGatheringsList();

        dispatch({ type: 'createGatheringEnd' });

        return true;
      }

      logError(
        `createGathering Error:${error?.message ?? error}`, // NOSONAR error stringified for logging
        'SingleCaseProvider.createGathering.error',
        {
          csid,
          err: error?.message ?? String(error), // NOSONAR error stringified for logging
          ...(error?.status && { status: error.status }),
        },
      );
      return null; // callback(error);
    },
    [state.gatheringsList, state.ready, csid, getGatheringsList, cstmrPost, piid, rlid],
  );

  // Which discussion the case detail view is focused on. Driven both by the
  // discussion tabs and by clicking a message belonging to another discussion.
  const selectGathering = useCallback(
    (gthrId) => {
      dispatch({
        type: 'selectGathering',
        gthrId,
      });
    },
    [],
  );

  // The "public" flag is a derived attribute of the selected discussion computed
  // by the discussion selector; it is mirrored here so the chat warning banner
  // can read it without the enriched discussion object.
  const setSelectedPublic = useCallback(
    (isPublic) => {
      dispatch({
        type: 'setSelectedPublic',
        public: isPublic,
      });
    },
    [],
  );

  // useEffect(() => {
  //   console.log('SingleCaseProvider useEffect');
  // }, []);

  // useEffect(() => {
  //   console.log(`SingleCaseProvider thisCase=${displayJson(state.thisCase)}`);
  // }, [state.thisCase]);

  //
  // Initial refresh
  //
  useEffect(
    () => {
      if (caseSelected?.csid
          && (csid !== caseSelected?.csid)) {
        console.log(`SingleCaseProvider init setThisCase piid=${
          caseSelected?.piid} csid=${caseSelected?.csid}`);

        dispatch({
          type: 'initFetch',
          piid: caseSelected?.piid,
          csid: caseSelected?.csid,
        });

        // Use void to make it clear we do not want to wait this async function:
        void refreshSingleCase(
          caseSelected.piid,
          caseSelected.csid,
        );
      }
    },
    [
      caseSelected?.piid, caseSelected?.csid, csid, refreshSingleCase,
    ],
  );

  const refreshAllCaseInfo = useCallback(
    async (callback, refreshDiscussions = true) => {
      if (!(ready && csid)) {
        console.log(`SingleCaseProvider - refreshAllCaseInfo for csid=${csid} - ignore`);
        return callback();
      }

      await refreshSingleCase(piid, csid);

      if (refreshDiscussions) await getGatheringsList();

      return callback();
    },
    [
      ready, piid, csid, refreshSingleCase, getGatheringsList,
    ],
  );

  // Polling is the fallback: only while the WS channel is down. While connected,
  // the open case refreshes on its own case notifications below.
  useIntervalAtMost(
    refreshAllCaseInfo,
    (state.ready && (!wsConnected)) ? (1000 * refreshPeriodS) : undefined,
  );

  const refreshAllCaseInfoRef = useRef(refreshAllCaseInfo);
  refreshAllCaseInfoRef.current = refreshAllCaseInfo;

  // Debounce this case's notifications into one refresh.
  const caseInfoTimer = useRef(null);
  const discussionsPendingRef = useRef(false);
  const onCaseInfoTrigger = useCallback(
    (data) => {
      if ((data?.type === 'caseUpdate') || (data?.type === 'caseSilentUpdate')) discussionsPendingRef.current = true;
      if (caseInfoTimer.current) clearTimeout(caseInfoTimer.current);
      caseInfoTimer.current = setTimeout(
        () => {
          caseInfoTimer.current = null;
          const refreshDiscussions = discussionsPendingRef.current;
          discussionsPendingRef.current = false;
          // Use void to make it clear we do not want to wait this async function:
          void refreshAllCaseInfoRef.current(() => {}, refreshDiscussions);
        },
        caseInfoRefreshDebounceMs,
      );
    },
    [],
  );

  const caseFilter = useMemo(() => ((csid) ? { csid } : undefined), [csid]);

  useCaseRefreshOnPush(
    onCaseInfoTrigger,
    {
      enabled: (ready && Boolean(csid)),
      filter: caseFilter,
    },
  );

  // Initial full load per case: the poll is gated off while WS is up, and the
  // init effect above fetches case detail but not the gatherings list.
  useEffect(
    () => {
      // Use void to make it clear we do not want to wait this async function:
      if (ready && csid) void refreshAllCaseInfoRef.current(() => {});
    },
    [ready, csid],
  );

  // On WS (re)connect, reconcile anything missed while the channel was down.
  const wasWsConnectedRef = useRef(false);
  useEffect(
    () => {
      if (wsConnected && (!wasWsConnectedRef.current)) {
        // Use void to make it clear we do not want to wait this async function:
        void refreshAllCaseInfoRef.current(() => {});
      }
      wasWsConnectedRef.current = wsConnected;
    },
    [wsConnected],
  );

  useEffect(
    () => () => {
      if (caseInfoTimer.current) clearTimeout(caseInfoTimer.current);
    },
    [],
  );

  //
  // This case is being looked at
  // When new message notification is set, clear it
  //
  useEffect(
    () => {
      // console.log(`SingleCaseProvider csid=${csid} state.thisCase.evntIdRdy=${
      //   state.thisCase.evntIdRdy} state.thisCase.evntIdFetched=${state.thisCase.evntIdFetched}`);
      if (state.ready
          && csid
          && state.thisCase.evntIdRdy
          && (state.thisCase.evntIdRdy !== state.thisCase.evntIdFetched)) {
        // Use void to make it clear we do not want to wait this async function:
        void updateEvntIdFetched(state.thisCase.evntIdRdy);
      }
    },
    [
      csid, state.ready, state.thisCase.evntIdFetched,
      state.thisCase.evntIdRdy, updateEvntIdFetched,
    ],
  );

  // useEffect(
  //   () => {
  //     console.log(`SingleCaseProvider thisCase=${
  //       displayJson(state.thisCase)}`);
  //   },
  //   [state.thisCase],
  // );

  const commandCallback = useCallback(
    // API sends back modified attributes, inject them back into modified case
    (error, rslt) => {
      if (!error) {
        dispatch({
          type: 'adjustCase',
          case: rslt,
        });
      }
      dispatch({ type: 'commandEnd' });
    },
    [],
  );

  const handle = useCallback(
    async () => {
      if (!state.commandPending) {
        dispatch({ type: 'commandStart' });
        const rslt = await participateCase();

        const modifiedCase = { piid, csid, ...rslt };

        commandCallback(!rslt, modifiedCase);

        return modifiedCase;
      }
      return null;
    },
    [state.commandPending, piid, csid, commandCallback, participateCase],
  );

  const hold = useCallback(
    async () => {
      if (!state.commandPending) {
        dispatch({ type: 'commandStart' });

        await ensureJoined();

        const rslt = await holdCase();

        commandCallback(!rslt, { piid, csid, ...rslt });
      }
    },
    [state.commandPending, piid, csid, commandCallback, holdCase, ensureJoined],
  );

  const requalify = useCallback(
    async (chng) => {
      if (!state.commandPending) {
        dispatch({ type: 'commandStart' });

        await ensureJoined();

        const rslt = await changeCase(chng);

        commandCallback(!rslt, { piid, csid, ...rslt });
      }
    },
    [state.commandPending, piid, csid, commandCallback, changeCase, ensureJoined],
  );

  // A task event is a case event: it takes the same command path as any other
  // change to the case, so the refetch, the read pointer and the rows the lists
  // hold behave the same whatever moved the case forward
  const changeTask = useCallback(
    async (task, chngLvl) => {
      if (state.commandPending) return null;
      dispatch({ type: 'commandStart' });

      await ensureJoined();

      const rslt = await changeTaskRequest(task, chngLvl);

      commandCallback(!rslt, { piid, csid, ...rslt });
      return rslt;
    },
    [state.commandPending, piid, csid, commandCallback, changeTaskRequest, ensureJoined],
  );

  const reject = useCallback(
    async () => {
      if (!state.commandPending) {
        dispatch({ type: 'commandStart' });

        await ensureJoined();

        const rslt = await rejectCase();

        commandCallback(!rslt, { piid, csid, ...rslt });
      }
    },
    [state.commandPending, piid, csid, commandCallback, rejectCase, ensureJoined],
  );

  const leave = useCallback(
    async () => {
      if (!state.commandPending) {
        dispatch({ type: 'commandStart' });

        const rslt = await leaveCase();

        commandCallback(!rslt, { piid, csid, ...rslt });
      }
    },
    [state.commandPending, piid, csid, commandCallback, leaveCase],
  );

  const rejoin = useCallback(
    async () => {
      if (!state.commandPending) {
        dispatch({ type: 'commandStart' });

        const rslt = await rejoinCase();

        commandCallback(!rslt, { piid, csid, ...rslt });
      }
    },
    [state.commandPending, piid, csid, commandCallback, rejoinCase],
  );

  const cancel = useCallback(
    async () => {
      if (!state.commandPending) {
        dispatch({ type: 'commandStart' });

        await ensureJoined();

        const rslt = await cancelCase();

        commandCallback(!rslt, { piid, csid, ...rslt });
      }
    },
    [state.commandPending, piid, csid, commandCallback, cancelCase, ensureJoined],
  );

  const close = useCallback(
    async () => {
      if (!state.commandPending) {
        dispatch({ type: 'commandStart' });

        await ensureJoined();

        const rslt = await closeCase(csid);

        commandCallback(!rslt, { piid, csid, ...rslt });
      }
    },
    [state.commandPending, piid, csid, commandCallback, closeCase, ensureJoined],
  );

  const resume = useCallback(
    async () => {
      if (!state.commandPending) {
        dispatch({ type: 'commandStart' });

        await ensureJoined();

        const rslt = await resumeCase();

        commandCallback(!rslt, { piid, csid, ...rslt });
      }
    },
    [state.commandPending, piid, csid, commandCallback, resumeCase, ensureJoined],
  );

  const clearEmergency = useCallback(
    async (crUid) => {
      dispatch({ type: 'commandStart' });

      await ensureJoined();

      await clearCaseEmergency(crUid);

      dispatch({ type: 'commandEnd' });
    },
    [clearCaseEmergency, ensureJoined],
  );

  const toggleVideo = useCallback(
    async () => {
      if (!state.commandPending) {
        const newVideoEn = (state.thisCase.videoEn) ? 0 : 1;

        dispatch({ type: 'commandStart' });

        await ensureJoined();

        const rslt = await setCaseVideoEn(newVideoEn);

        commandCallback(
          !rslt,
          {
            csid,
            videoEn: newVideoEn,
          },
        );
      }
    },
    [
      state.commandPending, csid,
      commandCallback, state.thisCase.videoEn, setCaseVideoEn, ensureJoined,
    ],
  );

  const addParticipants = useCallback(
    async (changes) => {
      const addCaseParticipants = async () => {
        let error;
        let res;
        try {
          // console.log(`Resume csid=${csid} at ${url}`);
          res = await cstmrPost({
            piid,
            rlid,
            urlPath: config.town.back.cases.handling,
            payload: {
              csid,
              ctime: isoDatetimeUtc(0, 1),
              eventCapturedAt: performance.now(),
              changes,
            },
          });
          if (res.status !== 200) {
            error = new Error(`Response status=${res.status}`);
          }
        } catch (err) {
          error = err;
        }

        if (!error) {
          // console.log(`addParticipants res.body.rslt=${
          //   displayJson(res.body.rslt, 4)
          // }`);
          return (res.body.rslt.data[0]);
        }
        logError(
          `addParticipants Error:${error?.message ?? error}`, // NOSONAR error stringified for logging
          'SingleCaseProvider.addParticipants.error',
          {
            csid,
            err: error?.message ?? String(error), // NOSONAR error stringified for logging
            ...(error?.status && { status: error.status }),
          },
        );
        return false;
      };

      if (!state.commandPending) {
        dispatch({ type: 'commandStart' });

        await ensureJoined();

        const rslt = await addCaseParticipants();

        // Only the touched keys: the reducer merges them into the case as it
        // stands, which an auto-join may have moved on from
        const modifiedCase = { piid, csid };
        if (changes.addGcatCode) {
          modifiedCase.gcatCode = [
            ...state.thisCase.gcatCode,
            ...changes.addGcatCode,
          ];
        }
        if (changes.addGrpGpid) {
          modifiedCase.grp = [
            ...state.thisCase.grp,
            ...changes.addGrpGpid.map((grpGpid) => ({
              grpGpid,
              invited: 1,
            })),
          ];
        }

        commandCallback(!rslt, modifiedCase);
      }
    },
    [
      state.commandPending, csid, state.thisCase, commandCallback, piid, rlid,
      cstmrPost, ensureJoined,
    ],
  );

  const callStart = useCallback(
    async (crUid) => {
      console.log('SingleCaseStore call');

      if (!state.commandPending) {
        console.log('start');
        dispatch({ type: 'commandStart' });

        const myCrUid = await ensureJoined();

        console.log('calls.start');
        const rslt = await calls.start(
          csid,
          myCrUid,
          crUid,
        );
        commandCallback(!rslt, { csid, ...rslt });
      }
    },
    [state.commandPending, csid, calls, commandCallback, ensureJoined],
  );

  const callPickup = useCallback(
    async (call) => {
      console.log('SingleCaseStore pickup');

      if (!state.commandPending) {
        console.log('start');
        dispatch({ type: 'commandStart' });

        const myCrUid = await ensureJoined();

        console.log('calls.pickup');
        const rslt = await calls.pickup(
          csid,
          myCrUid,
          call,
        );
        commandCallback(!rslt, { csid, ...rslt });
      }
    },
    [state.commandPending, csid, calls, commandCallback, ensureJoined],
  );

  const contextValue = useMemo(
    () => ({
      thisCase: {
        ready: state.ready,
        // The partnership is what the provider was opened on, not something the
        // fetched row carries: task views read it from here to reach the right server
        piid,
        // Default, when not in thisCase:
        cuid: undefined,
        csid: undefined,
        participants: [],

        // From thisCase:
        ...state.thisCase,

        // From this module:
        refreshPending: state.refreshPending,
        caseAutoJoin,
        ensureJoined,
        handle,
        hold,
        resume,
        close,
        cancel,
        addParticipants,
        requalify,
        reject,
        changeTask,
        sendCasePicture,
        blockUser,
        // /users is closed to partner sessions: blocking a person is the
        // inviter's own moderation, not a visiting team's
        canBlockUser: (!piid),
        leave,
        rejoin,
        clearEmergency,
        createAlert,
        toggleVideo,
        commandPending: state.commandPending,
        allGatherings: {
          ready: !!state.gatheringsList,
          list: state.gatheringsList,
          create: createGathering,
          selectedGthrId: state.selectedGthrId,
          selectedPublic: state.selectedPublic,
          select: selectGathering,
          setSelectedPublic,
        },
        // Callers that change who is on the case ask for it: an assignment adds
        // a participant, and the event naming him renders before the debounced
        // notification refresh would have brought him in
        refreshCaseInfo: refreshAllCaseInfo,
        callStart,
        callPickup,
        // callEnd,
        // callReject,
        // Simplified list, changing less often:
        chatParticipantsRef,
      },
    }),
    [
      addParticipants, callPickup, callStart, cancel, clearEmergency, close,
      caseAutoJoin, ensureJoined,
      createAlert, handle, hold, leave, reject, changeTask, sendCasePicture, blockUser,
      piid, refreshAllCaseInfo,
      requalify, resume,
      state.commandPending,
      state.ready, state.refreshPending, state.thisCase, toggleVideo, rejoin,
      state.gatheringsList, createGathering,
      state.selectedGthrId, state.selectedPublic, selectGathering, setSelectedPublic,
    ],
  );

  if (!state.ready) {
    return (
      <SingleCaseContext.Provider
        value={{
          thisCase: {
            ...singleCaseContextDefaultValue.thisCase,
            refreshPending: state.refreshPending,
            loadError: state.loadError,
          },
        }}
      >
        {children}
      </SingleCaseContext.Provider>
    );
  }

  return (
    <SingleCaseContext.Provider value={contextValue}>
      {children}
    </SingleCaseContext.Provider>
  );
}

SingleCaseProvider.propTypes = {
  children: PropTypes.node.isRequired,
  caseSelected: PropTypes.shape({
    piid: PropTypes.string,
    csid: PropTypes.string.isRequired,
  }),
  log: PropTypes.string,
  cstmrType: PropTypes.string.isRequired,
  onCaseUnavailable: PropTypes.func,
};

export default SingleCaseProvider;
